import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ROUTES_NAME from "../../constants/RoutesName";
import { fetchCenterFeedbackQuestions, submitCenterFeedback } from "../../services/service";
import { decryptData, encryptData } from "../../crypto";
import Loader from "../../components/Loader";

export default function CenterFeedbackPage({ questions: initialQuestions }) {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [openAlert, setOpenAlert] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("center_feedback") !== "yes") {
      navigate(ROUTES_NAME.THANK_YOU, { replace: true });
      return;
    }

    const formatQuestionsList = (list) => {
      if (!Array.isArray(list)) return [];
      return list.map((q) => ({
        id: q?.id,
        question: q?.question_en || q?.question_hi || "",
        options: Array.isArray(q?.options)
          ? q.options.map((opt) => ({
            id: opt?.id,
            text: opt?.option_en || opt?.option_hi || ""
          }))
          : []
      }));
    };

    if (initialQuestions && initialQuestions.length > 0) {
      setQuestions(formatQuestionsList(initialQuestions));
      setLoading(false);
      return;
    }

    const loadQuestions = async () => {
      const demoExamId = sessionStorage.getItem("demoExamId");
      const examId = sessionStorage.getItem("examId");
      const targetId = demoExamId || examId;
      if (!targetId) {
        setQuestions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await fetchCenterFeedbackQuestions(targetId);
        let fetchedList = [];
        if (response) {
          if (Array.isArray(response?.questions)) {
            fetchedList = response.questions;
          } else if (Array.isArray(response?.data)) {
            fetchedList = response.data;
          } else if (Array.isArray(response)) {
            fetchedList = response;
          }
        }

        if (fetchedList.length > 0) {
          setQuestions(formatQuestionsList(fetchedList));
        } else {
          setQuestions([]);
        }
      } catch (error) {
        console.error("Failed to load center feedback questions:", error);
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, [navigate, initialQuestions]);

  const handleOptionChange = (index, value) => {
    setSelectedOptions((prev) => ({ ...prev, [index]: value }));
  };

  const handleSubmit = async () => {
    // Check if all questions are answered
    const unansweredIndex = questions.findIndex((_, i) => !selectedOptions[i]);
    if (unansweredIndex !== -1) {
      setOpenAlert(true);
      return;
    }

    const demoExamId = sessionStorage.getItem("demoExamId");
    const examId = sessionStorage.getItem("examId");
    const targetId = demoExamId || examId;

    // Construct payload
    const payload = {
      exam_id: targetId ? (isNaN(Number(targetId)) ? targetId : Number(targetId)) : null,
      attempts: questions.map((q, i) => {
        const selectedOptId = selectedOptions[i];
        let optIdToSend = selectedOptId;
        // Convert to number if possible (since API expects integer option_id)
        if (optIdToSend !== undefined && optIdToSend !== null) {
          if (typeof optIdToSend === 'string' && !isNaN(Number(optIdToSend)) && optIdToSend.trim() !== "") {
            optIdToSend = Number(optIdToSend);
          }
        }

        return {
          question_id: q.id ? (isNaN(Number(q.id)) ? q.id : Number(q.id)) : null,
          option_id: optIdToSend || null
        };
      })
    };

    setLoading(true);
    try {
      if (targetId) {
        await submitCenterFeedback(payload);
      }
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    } finally {
      // Cleanup sessionStorage so they are logged out of the exam session but kept in dashboard
      const branch_id = sessionStorage.getItem("selectedBranch");
      const center_id = sessionStorage.getItem("selectedCenter");
      const userAuth = JSON.parse(sessionStorage.getItem("userAuth"));
      const exam_name = decryptData(sessionStorage.getItem("exam_name"));
      const userId = sessionStorage.getItem("userId");

      sessionStorage.clear();
      if (exam_name) {
        sessionStorage.setItem("exam_name", encryptData(exam_name));
      }
      sessionStorage.setItem("selectedBranch", branch_id);
      sessionStorage.setItem("selectedCenter", center_id);
      sessionStorage.setItem("userAuth", JSON.stringify(userAuth));
      sessionStorage.setItem("userId", userId);
      sessionStorage.setItem("candidateLoggedinToDashboard", true);

      setLoading(false);
      navigate(ROUTES_NAME.THANK_YOU);
    }
  };

  if (loading) {
    return <Loader message="Loading feedback questions..." />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        backgroundColor: "#f4f6fb",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ── Page title (no colored bar) ── */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 4, pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#1C2536" }}>
          Share Your Feedback
        </Typography>
        <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.4 }}>
          Help us improve — your response is completely anonymous.
        </Typography>
      </Box>

      {/* ── Questions area ── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, sm: 4, md: 6 },
          py: 2,
        }}
      >
        {questions.map(({ question, options }, index) => (
          <Box key={index}>
            <Box sx={{ py: 3 }}>
              {/* Question text */}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: "#1C2536",
                  mb: 2,
                  fontSize: { xs: "0.95rem", sm: "1rem" },
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    // backgroundColor: "#3f50b5",
                    color: "#000",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Box>
                <span>
                  {question}
                  <Box component="span" sx={{ color: "#d32f2f", ml: 0.5 }}>
                    *
                  </Box>
                </span>
              </Typography>

              {/* Radio options — horizontal row */}
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={selectedOptions[index] ?? ""}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  sx={{ gap: 1, flexWrap: "wrap" }}
                >
                  {options.map((option, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mr: 3,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          color: "#1C2536",
                          mr: 0.5,
                        }}
                      >
                        {String.fromCharCode(65 + i)}.
                      </Typography>
                      <FormControlLabel
                        value={option.id}
                        sx={{
                          margin: 0,
                        }}
                        control={
                          <Radio
                            size="small"
                            sx={{
                              color: "#9ca3af",
                              "&.Mui-checked": { color: "#3f50b5" },
                              py: 0.5,
                              paddingLeft: 0,
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{
                              color:
                                String(selectedOptions[index]) === String(option.id)
                                  ? "#3f50b5"
                                  : "#374151",
                              fontWeight:
                                String(selectedOptions[index]) === String(option.id) ? 600 : 400,
                              fontSize: { xs: "0.875rem", sm: "0.9rem" },
                              ml: 0.5,
                            }}
                          >
                            {option.text}
                          </Typography>
                        }
                      />
                    </Box>
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>

            {index < questions.length - 1 && (
              <Divider sx={{ borderColor: "#e5e7eb" }} />
            )}
          </Box>
        ))}

        {/* Bottom submit button only */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 4,
            pb: 4,
          }}
        >
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#3f50b5",
              color: "white",
              px: 5,
              py: 1.2,
              fontWeight: 700,
              borderRadius: 2,
              fontSize: "0.95rem",
              "&:hover": { backgroundColor: "#2c3e99" },
            }}
          >
            Submit Feedback
          </Button>
        </Box>
      </Box>

      {/* Mandatory feedback alert dialog */}
      <Dialog
        open={openAlert}
        onClose={() => setOpenAlert(false)}
        aria-labelledby="feedback-validation-dialog-title"
        aria-describedby="feedback-validation-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1.5,
            minWidth: { xs: "280px", sm: "360px" },
          },
        }}
      >
        <DialogTitle
          id="feedback-validation-dialog-title"
          sx={{ fontWeight: 700, color: "#1C2536" }}
        >
          Mandatory Feedback
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="feedback-validation-dialog-description"
            sx={{ color: "#4B5563" }}
          >
            All questions are mandatory. Please answer every question before submitting your feedback.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenAlert(false)}
            variant="contained"
            sx={{
              backgroundColor: "#3f50b5",
              color: "white",
              fontWeight: 600,
              borderRadius: 2,
              px: 3,
              "&:hover": { backgroundColor: "#2c3e99" },
            }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
